/** Item Rationalisation (Demo Mode only, Range Planning) - central control screen for
 * cleaning up duplicate, unused, inactive or poor-quality item data across the estate,
 * while still letting sites build creative, site-specific menus. Reads additive seed data
 * from src/mocks/data/itemRationalisationData.ts. No backend persistence exists yet.
 *
 * Kept as a dedicated Range Planning screen, separate from Procurement > Catalogue
 * Governance (blocked items, off-contract spend, price approvals) - the two cover
 * different governance concerns and are cross-linked rather than merged. */

import { useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import DuplicatesTab from './components/DuplicatesTab';
import UnusedItemsTab from './components/UnusedItemsTab';
import MissingDataTab from './components/MissingDataTab';
import PriceAnomaliesTab from './components/PriceAnomaliesTab';
import SuggestedActionsTab from './components/SuggestedActionsTab';
import AIChecksPanel from '../../components/shared/aiChecks/AIChecksPanel';
import { aiChecksClient } from '../../app/services/api/aiChecks/aiChecksEngine';
import { AICheckFinding } from '../../app/services/api/aiChecks/aiChecksTypes';

enum ItemRationalisationTab {
  Duplicates = 'duplicates',
  UnusedItems = 'unused-items',
  MissingData = 'missing-data',
  PriceAnomalies = 'price-anomalies',
  SuggestedActions = 'suggested-actions',
  AiChecks = 'ai-checks',
}

const ItemRationalisation = () => {
  const { t } = useTranslation('productManagement');
  const [tabValue, setTabValue] = useState<ItemRationalisationTab>(ItemRationalisationTab.Duplicates);
  const [aiChecksFindings, setAiChecksFindings] = useState<AICheckFinding[]>([]);

  useEffect(() => {
    aiChecksClient.getEstateFindings().then(setAiChecksFindings);
  }, []);

  return (
    <PageArea>
      <PageTitle>{t('itemRationalisationTitle', 'Item Rationalisation')}</PageTitle>

      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mt: 2, mb: 4 }}>
        {t(
          'itemRationalisationIntro',
          'Clean up duplicate, unused, inactive or poor-quality item data while still allowing site-specific menu creativity.',
        )}
      </Typography>

      <Tabs value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 0,
          '& .MuiTab-root': { minWidth: 'auto', minHeight: 0, px: 2, py: 1.5 },
        }}>
        <Tab label={t('rationalisation.tabs.duplicates', 'Duplicates')}
          value={ItemRationalisationTab.Duplicates} />
        <Tab label={t('rationalisation.tabs.unusedItems', 'Unused Items')}
          value={ItemRationalisationTab.UnusedItems} />
        <Tab label={t('rationalisation.tabs.missingData', 'Missing Data')}
          value={ItemRationalisationTab.MissingData} />
        <Tab label={t('rationalisation.tabs.priceAnomalies', 'Price Anomalies')}
          value={ItemRationalisationTab.PriceAnomalies} />
        <Tab label={t('rationalisation.tabs.suggestedActions', 'Suggested Actions')}
          value={ItemRationalisationTab.SuggestedActions} />
        <Tab label={t('rationalisation.tabs.aiChecks', 'AI Checks')}
          value={ItemRationalisationTab.AiChecks} />
      </Tabs>

      {tabValue === ItemRationalisationTab.AiChecks ? (
        <AIChecksPanel findings={aiChecksFindings}
          title={t('rationalisation.aiChecksTitle', 'AI Recipe Checks - catalogue-wide impact')} />
      ) : (
        <Section>
          <Box sx={{ p: { xs: 4, md: 5 } }}>
            {tabValue === ItemRationalisationTab.Duplicates && <DuplicatesTab />}
            {tabValue === ItemRationalisationTab.UnusedItems && <UnusedItemsTab />}
            {tabValue === ItemRationalisationTab.MissingData && <MissingDataTab />}
            {tabValue === ItemRationalisationTab.PriceAnomalies && <PriceAnomaliesTab />}
            {tabValue === ItemRationalisationTab.SuggestedActions && <SuggestedActionsTab />}
          </Box>
        </Section>
      )}
    </PageArea>
  );
};

export default ItemRationalisation;
